import { bcs } from "@iota/iota-sdk/bcs";
import { IscAgentID, IscAssets } from "./bcs.js";
function newBag(tx, { packageId }) {
  const assetsBag = tx.moveCall({
    target: `${packageId}::assets_bag::new`,
    arguments: []
  });
  return assetsBag;
}
function coinFromAmount(tx, amount) {
  const [splitCoin] = tx.splitCoins(tx.gas, [tx.pure(bcs.U64.serialize(amount))]);
  return splitCoin;
}
function placeCoinInBag(tx, { packageId }, assetsBag, coinType, coin) {
  tx.moveCall({
    target: `${packageId}::assets_bag::place_coin`,
    typeArguments: [coinType],
    arguments: [tx.object(assetsBag), tx.object(coin)]
  });
}
function agentIdForEVM(address) {
  const agentID = IscAgentID.serialize({
    EthereumAddressAgentID: {
      eth: bcs.fixedArray(20, bcs.u8()).fromHex(address)
    }
  });
  return agentID.toBytes();
}
function createAndSendRequest(tx, { packageId, chainId }, contract, contractFunction, contractArgs, assetsBag, transfers, gasBudget) {
  const allowance = IscAssets.serialize({
    coins: new Map(transfers.map(([coinType, amount]) => [coinType, Number(amount)])),
    objects: /* @__PURE__ */ new Map()
    // Add objects here. Provide their ID as BCS encoded bytes *and* the _object type_ as string.
  });
  tx.moveCall({
    target: `${packageId}::request::create_and_send_request`,
    arguments: [
      tx.pure(bcs.Address.serialize(chainId)),
      tx.object(assetsBag),
      tx.pure(bcs.U32.serialize(contract)),
      tx.pure(bcs.U32.serialize(contractFunction)),
      tx.pure(bcs.vector(bcs.vector(bcs.u8())).serialize(contractArgs)),
      tx.pure(bcs.vector(bcs.u8()).serialize(allowance.toBytes())),
      tx.pure(bcs.U64.serialize(gasBudget))
    ]
  });
}
function takeCoinBalanceFromBag(tx, { packageId }, assetsBag, coinType, amount) {
  return tx.moveCall({
    target: `${packageId}::assets_bag::take_coin_balance`,
    typeArguments: [coinType],
    arguments: [tx.object(assetsBag), tx.pure(bcs.U64.serialize(amount))]
  });
}
function takeAllCoinBalanceFromBag(tx, { packageId }, assetsBag, coinType) {
  return tx.moveCall({
    target: `${packageId}::assets_bag::take_all_coin_balance`,
    typeArguments: [coinType],
    arguments: [tx.object(assetsBag)]
  });
}
function placeCoinBalanceInBag(tx, { packageId }, assetsBag, coinType, balance) {
  tx.moveCall({
    target: `${packageId}::assets_bag::place_coin_balance`,
    typeArguments: [coinType],
    arguments: [tx.object(assetsBag), tx.object(balance)]
  });
}
function placeAssetInBag(tx, { packageId }, assetsBag, assetType, asset) {
  tx.moveCall({
    target: `${packageId}::assets_bag::place_asset`,
    typeArguments: [assetType],
    arguments: [tx.object(assetsBag), tx.object(asset)]
  });
}
function takeAssetFromBag(tx, { packageId }, assetsBag, assetType, asset) {
  return tx.moveCall({
    target: `${packageId}::assets_bag::take_asset`,
    typeArguments: [assetType],
    arguments: [tx.object(assetsBag), tx.object(asset)]
  });
}
function getSizeOfBag(tx, { packageId }, assetsBag) {
  return tx.moveCall({
    target: `${packageId}::assets_bag::get_size`,
    arguments: [tx.object(assetsBag)]
  });
}
function destroyBag(tx, { packageId }, assetsBag) {
  return tx.moveCall({
    target: `${packageId}::assets_bag::destroy_empty`,
    arguments: [tx.object(assetsBag)]
  });
}
function startNewChain(tx, { packageId }, metadata, coin) {
  return tx.moveCall({
    target: `${packageId}::anchor::start_new_chain`,
    arguments: [
      bcs.vector(bcs.u8()).serialize(metadata),
      coin ? tx.object(coin) : bcs.option(bcs.ObjectArg).serialize(null)
    ]
  });
}
function createAnchorWithAssetBag(tx, { packageId }, assetsBag) {
  return tx.moveCall({
    target: `${packageId}::anchor::create_anchor_with_assets_bag_ref`,
    arguments: [tx.object(assetsBag)]
  });
}
function updateAnchorStateForMigraton(tx, { packageId }, anchor, metadata, stateIndex) {
  return tx.moveCall({
    target: `${packageId}::anchor::update_anchor_state_for_migration`,
    arguments: [
      tx.object(anchor),
      bcs.vector(bcs.u8()).serialize(metadata),
      bcs.u32().serialize(stateIndex)
    ]
  });
}
function destroyAnchor(tx, { packageId }, anchor) {
  return tx.moveCall({
    target: `${packageId}::anchor::destroy`,
    arguments: [tx.object(anchor)]
  });
}
function borrowAssets(tx, { packageId }, anchor) {
  return tx.moveCall({
    target: `${packageId}::anchor::borrow_assets`,
    arguments: [tx.object(anchor)]
  });
}
function returnAssetsFromBorrow(tx, { packageId }, anchor, assetsBag, borrow) {
  return tx.moveCall({
    target: `${packageId}::anchor::return_assets_from_borrow`,
    arguments: [tx.object(anchor), tx.object(assetsBag), tx.object(borrow)]
  });
}
function receiveRequest(tx, { packageId }, anchor, request) {
  return tx.moveCall({
    target: `${packageId}::anchor::receive_request`,
    arguments: [tx.object(anchor), tx.object(request)]
  });
}
function transition(tx, { packageId }, anchor, newStateMetadata, receipts) {
  return tx.moveCall({
    target: `${packageId}::anchor::transition`,
    arguments: [
      tx.object(anchor),
      bcs.vector(bcs.u8()).serialize(newStateMetadata),
      tx.object(receipts)
    ]
  });
}
function placeCoinForMigration(tx, { packageId }, anchor, coinType, coin) {
  return tx.moveCall({
    target: `${packageId}::anchor::place_coin_for_migration`,
    typeArguments: [coinType],
    arguments: [tx.object(anchor), tx.object(coin)]
  });
}
function placeCoinBalanceForMigration(tx, { packageId }, anchor, coinType, balance) {
  return tx.moveCall({
    target: `${packageId}::anchor::place_coin_balance_for_migration`,
    typeArguments: [coinType],
    arguments: [tx.object(anchor), tx.object(balance)]
  });
}
function placeAssetForMigration(tx, { packageId }, anchor, assetType, asset) {
  return tx.moveCall({
    target: `${packageId}::anchor::place_asset_for_migration`,
    typeArguments: [assetType],
    arguments: [tx.object(anchor), tx.object(asset)]
  });
}
export {
  agentIdForEVM,
  borrowAssets,
  coinFromAmount,
  createAnchorWithAssetBag,
  createAndSendRequest,
  destroyAnchor,
  destroyBag,
  getSizeOfBag,
  newBag,
  placeAssetForMigration,
  placeAssetInBag,
  placeCoinBalanceForMigration,
  placeCoinBalanceInBag,
  placeCoinForMigration,
  placeCoinInBag,
  receiveRequest,
  returnAssetsFromBorrow,
  startNewChain,
  takeAllCoinBalanceFromBag,
  takeAssetFromBag,
  takeCoinBalanceFromBag,
  transition,
  updateAnchorStateForMigraton
};
//# sourceMappingURL=isc.js.map
